import { API_BASE, api } from "@/lib/apiClient";
import type { DokumentTyp } from "@/lib/dokumente";
import { DOKUMENT_TYP_LABELS } from "@/lib/dokumente";

/**
 * Dokumenttypen mit konfigurierbarem Layout/Briefpapier. Umfasst neben den
 * regulären Geschäftsdokumenten auch Mahnungen (kein eigener DokumentTyp,
 * da Mahnungen nicht in den Dokumentlisten/Erstell-Dialogen auftauchen).
 */
export type GdLayoutTyp = DokumentTyp | "mahnung";

export function gdLayoutTypLabel(typ: GdLayoutTyp): string {
  if (typ === "mahnung") return "Mahnung / Zahlungserinnerung";
  return DOKUMENT_TYP_LABELS[typ] ?? "Dokument";
}

export type GdPreviewPage = "erste" | "folge";

export interface GdPageLayout {
  margin_top_mm: number;
  margin_bottom_mm: number;
  margin_left_mm: number;
  margin_right_mm: number;
  block_top_mm: number;
  block_left_mm: number;
  block_right_mm: number;
}

export interface GdBetreffzeile {
  anzeigen: boolean;
  fett: boolean;
  schriftart: string;
  schriftgroesse_pt: number;
  quelle?: string;
  praefix?: string;
  format?: string;
}

/** Schriftstil für Bezeichnung oder Langtext einer Positionsart. */
export interface GdPositionstextStyle {
  schriftart: string;
  schriftgroesse_pt: number;
  schriftfarbe: string;
  fett: boolean;
  kursiv: boolean;
  unterstrichen: boolean;
}

export type GdPositionstextTypKey = "titel" | "text" | "artikel" | "leistung";

/** Formatierung pro Positionsart (Bezeichnung + Langtext). */
export interface GdPositionstextTyp {
  bezeichnung: GdPositionstextStyle;
  langtext: GdPositionstextStyle;
}

export type GdPositionstext = Record<GdPositionstextTypKey, GdPositionstextTyp>;

export interface GdLogoFirma {
  logo_anzeigen: boolean;
  anschrift_anzeigen: boolean;
  position_oben_mm: number;
  position_links_mm: number;
  firmenzeilen: string;
}

export interface GdInfoblock {
  anzeigen: boolean;
  ansprechpartner_bild: boolean;
  position_oben_mm: number;
  position_links_mm: number;
  breite_mm: number;
  feld_projektnummer: boolean;
  feld_referenzdokument: boolean;
  feld_kundennummer: boolean;
  feld_bestellnummer: boolean;
  feld_kundenreferenz: boolean;
  feld_ust_id: boolean;
  feld_dokumentnummer: boolean;
  feld_projektansprechpartner: boolean;
  leistungsdatum_modus: string;
  zahlungsart_modus: string;
  ansprechpartner_modus: string;
  kontakt_name: boolean;
  kontakt_telefon: boolean;
  kontakt_mobil: boolean;
  kontakt_fax: boolean;
  kontakt_email: boolean;
}

export interface GdZusaetzlicheOptionen {
  betreff_abschlussrechnung: string;
  betreff_teilrechnung: string;
  betreff_stornorechnung: string;
  betreff_vorkasse: string;
  rechnungsuebersicht_anzeigen: boolean;
  lohn_maschinenkosten_ausweisen: boolean;
  lohn_maschinenkosten_text: string;
  /** Zahlungsart-Default: rechnung | vorkasse */
  zahlungsart: string;
  zahlungsziel_tage: number;
  skonto_prozent: number;
  skontoziel_tage: number;
  /** Automatischen Zahlungsbedingungen-Hinweis („Zahlbar innerhalb von …“) anzeigen. */
  zahlungsbedingungen_anzeigen: boolean;
  /** Bankinformationen im Dokumentenfuß anzeigen. */
  bank_anzeigen: boolean;
  bank_kontoinhaber: string;
  bank_kreditinstitut: string;
  bank_iban: string;
  bank_bic: string;
  verkaeufer_ust_id: string;
  /** Steuernummer als Fallback zur USt-IdNr. (E-Rechnung, BT-32). */
  verkaeufer_steuernummer: string;
  /** Verkäuferkontakt für die E-Rechnung (XRechnung-Pflicht BT-41/42/43). */
  verkaeufer_kontakt_name: string;
  verkaeufer_kontakt_telefon: string;
  verkaeufer_kontakt_email: string;
  /** PDF-Dateiname mit Platzhaltern, z. B. {{Projekt.nummer}}-{{Dokument.typ}}-{{Dokument.nummer}}.pdf */
  pdf_dateiname: string;
}

export const DEFAULT_PDF_DATEINAME =
  "{{Projekt.nummer}}-{{Dokument.typ}}-{{Dokument.nummer}}.pdf";

export interface GdQrSignatur {
  /** Kleiner QR-Code als Seitenmarker (jede Seite). */
  mini_qr_anzeigen: boolean;
  /** Abstand vom oberen Blattrand (physisch, 0 = Kante). */
  mini_qr_oben_mm: number;
  /** Abstand vom rechten Blattrand (physisch, 0 = Kante). */
  mini_qr_rechts_mm: number;
  mini_qr_groesse_mm: number;
  /** Signaturbereich auf der letzten Seite (Position + Felder). */
  signaturblock_anzeigen: boolean;
  mitarbeiter_unterschrift_anzeigen: boolean;
  mitarbeiter_unterschrift_label: string;
  kunden_unterschrift_anzeigen: boolean;
  /** Beschriftung Kundenunterschrift. */
  signatur_label: string;
  /** Abstand vom linken Blattrand auf der letzten Seite. */
  signatur_links_mm: number;
  /** Abstand vom unteren Blattrand auf der letzten Seite. */
  signatur_unten_mm: number;
  signatur_breite_mm: number;
  grosser_qr_anzeigen: boolean;
  grosser_qr_hinweis: string;
}

export interface GdLayoutConfig {
  dokument_typ?: GdLayoutTyp;
  font_family: string;
  font_size_pt: number;
  /** Zeilenhöhe der Empfängeradresse (CSS line-height, z. B. 1.2). */
  adresse_zeilenabstand: number;
  /** Zeilenhöhe von Einleitung und Schlusstext (CSS line-height). */
  text_zeilenabstand: number;
  /** Abstand zwischen Adressblock und Betreffzeile 1 in mm. */
  adresse_betreff_abstand_mm: number;
  /** Abstand zwischen Leistungsempfänger und Rechnungsnummer in mm. */
  leistungempfaenger_abstand_mm: number;
  show_fold_marks: boolean;
  briefpapier_erste_id: number | null;
  briefpapier_folge_id: number | null;
  erste_seite: GdPageLayout;
  folgeseite: GdPageLayout;
  betreffzeile1: GdBetreffzeile;
  betreffzeile2: GdBetreffzeile;
  positionstext: GdPositionstext;
  logo_firma: GdLogoFirma;
  infoblock: GdInfoblock;
  zusaetzliche_optionen: GdZusaetzlicheOptionen;
  qr_signatur: GdQrSignatur;
}

export interface GdBriefpapierMeta {
  id: number;
  dokument_typ: GdLayoutTyp;
  seite: GdPreviewPage;
  filename: string;
  content_type: string;
  size_bytes: number;
  uploaded_at: string;
}

export const GD_FONT_OPTIONS = [
  "Arial, Helvetica, sans-serif",
  "Helvetica, Arial, sans-serif",
  "Times New Roman, Times, serif",
  "Georgia, serif",
  "Verdana, Geneva, sans-serif",
];

export const GD_FONT_SIZE_OPTIONS = [9, 10, 11, 12];

/** Zeilenabstand Empfängeradresse (CSS line-height). */
export const GD_ADRESSE_ZEILENABSTAND_OPTIONS = [1.0, 1.1, 1.15, 1.2, 1.3, 1.4, 1.45, 1.5, 1.6, 1.8, 2.0];

/** Zeilenabstand Einleitung / Schlusstext (gleiche Stufen wie Adresse). */
export const GD_TEXT_ZEILENABSTAND_OPTIONS = GD_ADRESSE_ZEILENABSTAND_OPTIONS;

export const GD_BETREFF_FONT_SIZE_OPTIONS = [8, 9, 10, 11, 12, 13, 14, 16];

export const GD_POSITIONSTEXT_FONT_SIZE_OPTIONS = [7, 8, 9, 9.5, 10, 11, 12, 13, 14];

export const GD_BETREFFZEILE1_QUELLE_OPTIONS = [
  { value: "projektname", label: "Projektname" },
  { value: "kunde", label: "Kundenname" },
  { value: "auftrag", label: "Auftragsnummer" },
];

export const GD_LEISTUNGSDATUM_OPTIONS = [
  {
    value: "gleich_rechnungsdatum",
    label: "Zeitraum oder Datum, sonst „entspricht Rechnungsdatum“",
  },
  { value: "leistungsdatum", label: "Leistungsdatum anzeigen" },
  { value: "lieferdatum", label: "Lieferdatum anzeigen" },
  { value: "ausblenden", label: "Nicht anzeigen" },
];

export const GD_ZAHLUNGSART_OPTIONS = [
  { value: "ausblenden", label: "Nicht anzeigen" },
  { value: "anzeigen", label: "Zahlungsart anzeigen" },
];

export const GD_ANSPRECHPARTNER_OPTIONS = [
  { value: "dokument_ersteller", label: "Ersteller/in des Dokuments" },
  { value: "kunde_ansprechpartner", label: "Ansprechpartner/in des Kunden" },
  { value: "ausblenden", label: "Nicht anzeigen" },
];

export const DEFAULT_BETREFFZEILE1: GdBetreffzeile = {
  anzeigen: true,
  fett: true,
  schriftart: GD_FONT_OPTIONS[0] ?? "Arial, Helvetica, sans-serif",
  schriftgroesse_pt: 10,
  quelle: "projektname",
  praefix: "Projekt:",
};

export const DEFAULT_BETREFFZEILE2: GdBetreffzeile = {
  anzeigen: true,
  fett: true,
  schriftart: GD_FONT_OPTIONS[0] ?? "Arial, Helvetica, sans-serif",
  schriftgroesse_pt: 13,
  format: "{typ}-Nr. {nummer}",
};

export const DEFAULT_POSITIONSTEXT_BEZEICHNUNG: GdPositionstextStyle = {
  schriftart: GD_FONT_OPTIONS[0] ?? "Arial, Helvetica, sans-serif",
  schriftgroesse_pt: 9.5,
  schriftfarbe: "#111111",
  fett: true,
  kursiv: false,
  unterstrichen: false,
};

export const DEFAULT_POSITIONSTEXT_LANGTEXT: GdPositionstextStyle = {
  schriftart: GD_FONT_OPTIONS[0] ?? "Arial, Helvetica, sans-serif",
  schriftgroesse_pt: 9,
  schriftfarbe: "#636366",
  fett: false,
  kursiv: false,
  unterstrichen: false,
};

export const GD_POSITIONSTEXT_TYP_OPTIONS: { value: GdPositionstextTypKey; label: string }[] = [
  { value: "titel", label: "Titel" },
  { value: "text", label: "Text" },
  { value: "artikel", label: "Artikel" },
  { value: "leistung", label: "Leistung" },
];

function defaultPositionstextTyp(
  bezeichnung: GdPositionstextStyle = DEFAULT_POSITIONSTEXT_BEZEICHNUNG,
  langtext: GdPositionstextStyle = DEFAULT_POSITIONSTEXT_LANGTEXT,
): GdPositionstextTyp {
  return {
    bezeichnung: { ...bezeichnung },
    langtext: { ...langtext },
  };
}

export const DEFAULT_POSITIONSTEXT: GdPositionstext = {
  titel: defaultPositionstextTyp(),
  text: defaultPositionstextTyp({
    ...DEFAULT_POSITIONSTEXT_BEZEICHNUNG,
    fett: false,
    schriftgroesse_pt: 9,
  }),
  artikel: defaultPositionstextTyp(),
  leistung: defaultPositionstextTyp(),
};

function isLegacyPositionstextStyle(raw: unknown): raw is Partial<GdPositionstextStyle> {
  if (!raw || typeof raw !== "object") return false;
  const obj = raw as Record<string, unknown>;
  if (GD_POSITIONSTEXT_TYP_OPTIONS.some((opt) => opt.value in obj)) return false;
  return (
    "schriftart" in obj ||
    "schriftgroesse_pt" in obj ||
    "schriftfarbe" in obj ||
    "fett" in obj ||
    "kursiv" in obj ||
    "unterstrichen" in obj
  );
}

function normalizePositionstext(
  raw: Partial<GdPositionstext> | Partial<GdPositionstextStyle> | null | undefined,
): GdPositionstext {
  const base = {
    titel: defaultPositionstextTyp(),
    text: defaultPositionstextTyp({
      ...DEFAULT_POSITIONSTEXT_BEZEICHNUNG,
      fett: false,
      schriftgroesse_pt: 9,
    }),
    artikel: defaultPositionstextTyp(),
    leistung: defaultPositionstextTyp(),
  };

  if (!raw) return base;

  // Migration: flaches Alt-Format → Langtext aller Typen.
  if (isLegacyPositionstextStyle(raw)) {
    const legacy = { ...DEFAULT_POSITIONSTEXT_LANGTEXT, ...raw };
    return {
      titel: { ...base.titel, langtext: { ...legacy } },
      text: { ...base.text, langtext: { ...legacy } },
      artikel: { ...base.artikel, langtext: { ...legacy } },
      leistung: { ...base.leistung, langtext: { ...legacy } },
    };
  }

  const typed = raw as Partial<GdPositionstext>;
  const out = { ...base };
  for (const opt of GD_POSITIONSTEXT_TYP_OPTIONS) {
    const typRaw = typed[opt.value];
    if (!typRaw) continue;
    if (isLegacyPositionstextStyle(typRaw)) {
      out[opt.value] = {
        ...base[opt.value],
        langtext: { ...base[opt.value].langtext, ...typRaw },
      };
      continue;
    }
    out[opt.value] = {
      bezeichnung: {
        ...base[opt.value].bezeichnung,
        ...(typRaw.bezeichnung || {}),
      },
      langtext: {
        ...base[opt.value].langtext,
        ...(typRaw.langtext || {}),
      },
    };
  }
  return out;
}

export const DEFAULT_LOGO_FIRMA: GdLogoFirma = {
  logo_anzeigen: false,
  anschrift_anzeigen: true,
  position_oben_mm: 10,
  position_links_mm: 120,
  firmenzeilen:
    "Gebrüder Ernsthaus Stahl- und Metallbau GmbH\nIndustriestraße 1\n12345 Musterstadt",
};

export const DEFAULT_INFOBLOCK: GdInfoblock = {
  anzeigen: true,
  ansprechpartner_bild: false,
  position_oben_mm: 55,
  position_links_mm: 125,
  breite_mm: 75,
  feld_projektnummer: true,
  feld_referenzdokument: true,
  feld_kundennummer: true,
  feld_bestellnummer: true,
  feld_kundenreferenz: true,
  feld_ust_id: false,
  feld_dokumentnummer: false,
  feld_projektansprechpartner: true,
  leistungsdatum_modus: "gleich_rechnungsdatum",
  zahlungsart_modus: "ausblenden",
  ansprechpartner_modus: "dokument_ersteller",
  kontakt_name: true,
  kontakt_telefon: true,
  kontakt_mobil: true,
  kontakt_fax: false,
  kontakt_email: true,
};

export const DEFAULT_ZUSAETZLICHE_OPTIONEN: GdZusaetzlicheOptionen = {
  betreff_abschlussrechnung: "Schlussrechnung-Nr.",
  betreff_teilrechnung: "Teilrechnung-Nr.",
  betreff_stornorechnung: "Stornorechnung-Nr.",
  betreff_vorkasse: "Vorkasse-Rechnung-Nr.",
  rechnungsuebersicht_anzeigen: true,
  lohn_maschinenkosten_ausweisen: false,
  lohn_maschinenkosten_text:
    "Im Bruttobetrag sind {summe} Lohn- und Maschinenkosten enthalten.",
  zahlungsart: "rechnung",
  zahlungsziel_tage: 14,
  skonto_prozent: 0,
  skontoziel_tage: 0,
  zahlungsbedingungen_anzeigen: true,
  bank_anzeigen: true,
  bank_kontoinhaber: "Gebrüder Ernsthaus Stahl- und Metallbau GmbH",
  bank_kreditinstitut: "Frankfurter Sparkasse",
  bank_iban: "DE69 5005 0201 0000 3395 63",
  bank_bic: "HELADEF1822",
  verkaeufer_ust_id: "",
  verkaeufer_steuernummer: "",
  verkaeufer_kontakt_name: "",
  verkaeufer_kontakt_telefon: "",
  verkaeufer_kontakt_email: "",
  pdf_dateiname: DEFAULT_PDF_DATEINAME,
};

export function defaultGdQrSignatur(typ: GdLayoutTyp): GdQrSignatur {
  const enabled = typ === "lieferschein";
  const typLabel = gdLayoutTypLabel(typ);
  return {
    mini_qr_anzeigen: enabled,
    mini_qr_oben_mm: 8,
    mini_qr_rechts_mm: 12,
    mini_qr_groesse_mm: 10.5,
    signaturblock_anzeigen: enabled,
    mitarbeiter_unterschrift_anzeigen: false,
    mitarbeiter_unterschrift_label: "Mitarbeiter",
    kunden_unterschrift_anzeigen: enabled,
    signatur_label: "Auftraggeber / Empfänger",
    signatur_links_mm: 25,
    signatur_unten_mm: 25,
    signatur_breite_mm: 170,
    grosser_qr_anzeigen: enabled,
    grosser_qr_hinweis: `QR-Code scannen, um ${typLabel} digital zu prüfen und freizugeben.`,
  };
}

export const GD_ZAHLUNGSBEDINGUNG_ART_OPTIONS = [
  { value: "rechnung", label: "Rechnung" },
  { value: "vorkasse", label: "Vorkasse" },
];

export const DEFAULT_PAGE_LAYOUT: GdPageLayout = {
  margin_top_mm: 65,
  margin_bottom_mm: 35,
  margin_left_mm: 0,
  margin_right_mm: 0,
  block_top_mm: 100,
  block_left_mm: 25,
  block_right_mm: 20,
};

export const DEFAULT_FOLGE_PAGE_LAYOUT: GdPageLayout = {
  margin_top_mm: 20,
  margin_bottom_mm: 35,
  margin_left_mm: 0,
  margin_right_mm: 0,
  block_top_mm: 25,
  block_left_mm: 25,
  block_right_mm: 20,
};

export function defaultGdLayoutConfig(typ: GdLayoutTyp): GdLayoutConfig {
  return {
    dokument_typ: typ,
    font_family: GD_FONT_OPTIONS[0] ?? "Arial, Helvetica, sans-serif",
    font_size_pt: 10,
    adresse_zeilenabstand: 1.2,
    text_zeilenabstand: 1.15,
    adresse_betreff_abstand_mm: 5,
    leistungempfaenger_abstand_mm: 4,
    show_fold_marks: true,
    briefpapier_erste_id: null,
    briefpapier_folge_id: null,
    erste_seite: { ...DEFAULT_PAGE_LAYOUT },
    folgeseite: { ...DEFAULT_FOLGE_PAGE_LAYOUT },
    betreffzeile1: { ...DEFAULT_BETREFFZEILE1 },
    betreffzeile2: { ...DEFAULT_BETREFFZEILE2 },
    positionstext: normalizePositionstext(DEFAULT_POSITIONSTEXT),
    logo_firma: { ...DEFAULT_LOGO_FIRMA },
    infoblock: { ...DEFAULT_INFOBLOCK },
    zusaetzliche_optionen: { ...DEFAULT_ZUSAETZLICHE_OPTIONEN },
    qr_signatur: defaultGdQrSignatur(typ),
  };
}

/** Stellt sicher, dass alle Inhalts-Sektionen vorhanden sind (ältere gespeicherte Layouts). */
export function normalizeGdLayoutConfig(
  raw: Partial<GdLayoutConfig> | null | undefined,
  typ: GdLayoutTyp,
): GdLayoutConfig {
  const base = defaultGdLayoutConfig(typ);
  if (!raw) return base;
  const adresseZeilenabstand = Number(raw.adresse_zeilenabstand);
  const textZeilenabstand = Number(raw.text_zeilenabstand);
  const adresseBetreffAbstand = Number(raw.adresse_betreff_abstand_mm);
  const leistungempfaengerAbstand = Number(raw.leistungempfaenger_abstand_mm);
  return {
    ...base,
    ...raw,
    dokument_typ: typ,
    adresse_zeilenabstand:
      Number.isFinite(adresseZeilenabstand) && adresseZeilenabstand > 0
        ? Math.min(3, Math.max(0.8, adresseZeilenabstand))
        : base.adresse_zeilenabstand,
    text_zeilenabstand:
      Number.isFinite(textZeilenabstand) && textZeilenabstand > 0
        ? Math.min(3, Math.max(0.8, textZeilenabstand))
        : base.text_zeilenabstand,
    adresse_betreff_abstand_mm:
      Number.isFinite(adresseBetreffAbstand)
        ? Math.min(40, Math.max(0, adresseBetreffAbstand))
        : base.adresse_betreff_abstand_mm,
    leistungempfaenger_abstand_mm:
      Number.isFinite(leistungempfaengerAbstand)
        ? Math.min(40, Math.max(0, leistungempfaengerAbstand))
        : base.leistungempfaenger_abstand_mm,
    erste_seite: { ...base.erste_seite, ...raw.erste_seite },
    folgeseite: { ...base.folgeseite, ...raw.folgeseite },
    betreffzeile1: { ...base.betreffzeile1, ...raw.betreffzeile1 },
    betreffzeile2: { ...base.betreffzeile2, ...raw.betreffzeile2 },
    positionstext: normalizePositionstext(raw.positionstext ?? base.positionstext),
    logo_firma: { ...base.logo_firma, ...raw.logo_firma },
    infoblock: { ...base.infoblock, ...raw.infoblock },
    zusaetzliche_optionen: {
      ...base.zusaetzliche_optionen,
      ...raw.zusaetzliche_optionen,
      zahlungsbedingungen_anzeigen:
        raw.zusaetzliche_optionen?.zahlungsbedingungen_anzeigen ??
        base.zusaetzliche_optionen.zahlungsbedingungen_anzeigen,
      bank_anzeigen:
        raw.zusaetzliche_optionen?.bank_anzeigen ?? base.zusaetzliche_optionen.bank_anzeigen,
    },
    qr_signatur: (() => {
      const merged = {
        ...base.qr_signatur,
        ...raw.qr_signatur,
      };
      // Digitaler QR setzt Kundenunterschrift immer voraus.
      if (merged.grosser_qr_anzeigen) {
        merged.kunden_unterschrift_anzeigen = true;
      }
      // Ältere Layouts ohne kunden_*-Flag: bei aktivem Signaturblock Kundenfeld annehmen.
      if (
        raw.qr_signatur &&
        raw.qr_signatur.kunden_unterschrift_anzeigen === undefined &&
        raw.qr_signatur.mitarbeiter_unterschrift_anzeigen === undefined &&
        merged.signaturblock_anzeigen &&
        !merged.grosser_qr_anzeigen
      ) {
        merged.kunden_unterschrift_anzeigen = true;
      }
      return merged;
    })(),
  };
}

function buildApiUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  if (!API_BASE) return path;
  return `${API_BASE}${path.startsWith("/") ? "" : "/"}${path}`;
}

export async function getGdLayoutConfig(typ: GdLayoutTyp): Promise<GdLayoutConfig> {
  const res = await api.get<{ layout: GdLayoutConfig }>(`/dokumente/einstellungen/layout/${typ}`);
  return normalizeGdLayoutConfig(res.layout, typ);
}

export async function saveGdLayoutConfig(typ: GdLayoutTyp, layout: GdLayoutConfig): Promise<GdLayoutConfig> {
  const res = await api.put<{ layout: GdLayoutConfig }>(
    `/dokumente/einstellungen/layout/${typ}`,
    layout,
  );
  return res.layout || layout;
}

export async function copyGdLayoutConfig(
  zielTyp: GdLayoutTyp,
  quellTyp: GdLayoutTyp,
): Promise<GdLayoutConfig> {
  const res = await api.post<{ layout: GdLayoutConfig }>(
    `/dokumente/einstellungen/layout/${zielTyp}/kopieren`,
    { quell_typ: quellTyp },
  );
  return res.layout || defaultGdLayoutConfig(zielTyp);
}

/** Inhaltsmenge für die Layout-Vorschau (1 = wenig / oft 1 Seite, 10 = viel / mehrseitig). */
export type GdPreviewContentAmount = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

export async function fetchGdLayoutPreviewHtml(
  typ: GdLayoutTyp,
  layout: GdLayoutConfig,
  contentAmount: number = 3,
  signal?: AbortSignal,
): Promise<string> {
  const res = await fetch(buildApiUrl(`/dokumente/einstellungen/layout/${typ}/preview`), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ layout, content_amount: contentAmount }),
    signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.text();
}

/** PDF-Livevorschau – dieselbe WeasyPrint-Pipeline wie fertige Dokumente. */
export async function fetchGdLayoutPreviewPdf(
  typ: GdLayoutTyp,
  layout: GdLayoutConfig,
  contentAmount: number = 3,
  signal?: AbortSignal,
): Promise<Blob> {
  const res = await fetch(buildApiUrl(`/dokumente/einstellungen/layout/${typ}/preview-pdf`), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ layout, content_amount: contentAmount }),
    signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `${res.status} ${res.statusText}`);
  }
  return res.blob();
}

export async function uploadGdBriefpapier(
  typ: GdLayoutTyp,
  seite: GdPreviewPage,
  file: File,
): Promise<GdBriefpapierMeta> {
  const fd = new FormData();
  fd.set("dokument_typ", typ);
  fd.set("seite", seite);
  fd.set("file", file);
  return api.postForm<GdBriefpapierMeta>("/dokumente/einstellungen/briefpapier", fd);
}

export async function deleteGdBriefpapier(id: number): Promise<void> {
  await api.del(`/dokumente/einstellungen/briefpapier/${id}`);
}

export function getGdBriefpapierUrl(id: number | null | undefined): string | null {
  if (!id) return null;
  return buildApiUrl(`/dokumente/einstellungen/briefpapier/${id}`);
}

export async function fetchGdBriefpapierBlob(id: number): Promise<Blob> {
  const url = getGdBriefpapierUrl(id);
  if (!url) throw new Error("Kein Briefpapier");
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText}`);
  }
  return res.blob();
}

export function getGdBriefpapierPreviewUrl(id: number | null | undefined): string | null {
  if (!id) return null;
  return buildApiUrl(`/dokumente/einstellungen/briefpapier/${id}/vorschau`);
}

/** Lädt Briefpapier immer als PNG (PDF wird serverseitig gerendert). */
export async function fetchGdBriefpapierPreviewBlob(id: number): Promise<Blob> {
  const url = getGdBriefpapierPreviewUrl(id);
  if (!url) throw new Error("Kein Briefpapier");
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText}`);
  }
  return res.blob();
}

export const GD_DOKUMENT_TYP_OPTIONS: { value: GdLayoutTyp; label: string }[] = [
  ...(Object.keys(DOKUMENT_TYP_LABELS) as DokumentTyp[])
    .filter((value) => value !== "stueckliste")
    .map((value) => ({
      value: value as GdLayoutTyp,
      label: DOKUMENT_TYP_LABELS[value],
    })),
  { value: "mahnung", label: "Mahnung / Zahlungserinnerung" },
];

export type GdLayoutSection =
  | "allgemein"
  | "nummern"
  | "gestaltung"
  | "erste-seite"
  | "folgeseite"
  | "zusaetzliche-optionen"
  | "qr-signatur";

export const GD_LAYOUT_SECTIONS: { id: GdLayoutSection; label: string }[] = [
  { id: "allgemein", label: "Allgemeine Einstellungen" },
  { id: "nummern", label: "Nummernkreise" },
  { id: "gestaltung", label: "Allgemeine Gestaltung" },
  { id: "erste-seite", label: "Gestaltung – Erste Seite" },
  { id: "folgeseite", label: "Gestaltung – Folgeseiten" },
  { id: "zusaetzliche-optionen", label: "Zusätzliche Optionen" },
  { id: "qr-signatur", label: "QR-Code & Unterschrift" },
];

export function gdLayoutSectionsForTyp(typ: GdLayoutTyp): { id: GdLayoutSection; label: string }[] {
  if (typ === "stundennachweis" || typ === "stueckliste") return [];
  if (typ === "mahnung") {
    // Mahnungen haben keine Positionen/Nummernkreis-Startwerte und keinen QR-Freigabe-Flow.
    return GD_LAYOUT_SECTIONS.filter((s) => s.id !== "nummern" && s.id !== "qr-signatur");
  }
  return GD_LAYOUT_SECTIONS;
}
